import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

export type EtatIntegration =
  | 'IMMINENTE'
  | 'A_PREPARER'
  | 'PLANIFIEE'
  | 'DEMARREE'
  | 'SANS_DATE';

export interface LigneIntegration {
  apprenantId: string;
  apprenant: string;
  formation: string | null;
  siteCode: string;
  entreprise: string | null;
  dateEntree: string | null;
  dateEntreeLisible: string;
  decompte: string;
  jours: number | null;
  etat: EtatIntegration;
  etatLibelle: string;
  etatTon: string;
  salleIntegration: string | null;
  heureIntegration: string | null;
  formateurReferent: string | null;
  formateurReferentId: string | null;
  convocationEnvoyeeLe: string | null;
  convocationDocumentId: string | null;
  manques: string[];
  nouveauContrat: boolean;
}

export interface ReponseIntegration {
  entrees: LigneIntegration[];
  compteurs: {
    nouveauxContrats: number;
    imminentes: number;
    convocationsEnvoyees: number;
    sansFormateurReferent: number;
    preavisJours: number;
  };
  formateurs: { id: string; nom: string }[];
  documentsAApporter: string[];
}

export function useIntegration(site: string, imminentes: boolean) {
  const parametres = new URLSearchParams({ site });
  if (imminentes) parametres.set('imminentes', 'true');

  return useQuery({
    queryKey: ['integration', parametres.toString()],
    queryFn: () => appelApi<ReponseIntegration>(`/integration?${parametres.toString()}`),
  });
}

function invalider(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['integration'] });
  void client.invalidateQueries({ queryKey: ['apprenants'] });
}

export function usePreparerIntegration() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      apprenantId: string;
      salleIntegration?: string;
      heureIntegration?: string;
      formateurReferentId?: string | null;
      dateEntree?: string;
    }) => {
      const { apprenantId, ...corps } = variables;
      return appelApi<LigneIntegration>(`/integration/apprenants/${apprenantId}`, {
        methode: 'PATCH',
        corps,
      });
    },
    onSuccess: () => invalider(client),
  });
}

export function useEnvoyerConvocation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (apprenantId: string) =>
      appelApi<{ documentId: string; destinataires: number; message: string }>(
        `/integration/apprenants/${apprenantId}/convocation`,
        { methode: 'POST' },
      ),
    onSuccess: () => invalider(client),
  });
}
